import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai/index';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import styles from './css/index.module.scss';
import RightArrowBlue from '../../assets/icons/rightArrowBlue.svg';
import TonIcon from '../../assets/icons/ton.svg';
import RightArrow from '../../assets/icons/arrowRight.svg';
import EmptyHeight from '../../components/EmptyHeight';
import convertPrice from '../../helpers/convertPrice';
import { DEFAULT_USERNAMES } from '../../mock/usernames';
import MyLoading from '../../components/MyLoading';
import {
  myNumbersFiltersAtom, numbersAtom, numbersFiltersAtom, selectedNumberAtom,
} from '../../atoms/numbersAtoms';
import convertPhoneNumber from '../../helpers/convertPhoneNumber';
import MyButton from '../../components/MyButton';
import MyInput from '../../components/MyInput';
import useInputState from '../../hooks/UseInputState';
import { modalStateAtom } from '../../atoms/globalAtoms';
import SuccessTransactionModal from './components/SuccessTransactionModal';
import sleep from '../../helpers/sleep';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../api';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import tStatic from '../../translations';

const PLANS = [
  {
    countText: `1 ${tStatic(DICTIONARY.wallet)}`,
    priceText: '15 TON',
    tonAmount: 15,
  },
  {
    countText: `3 ${tStatic(DICTIONARY.wallets)}`,
    priceText: '40 TON',
    tonAmount: 40,
  },
  {
    countText: `9 ${tStatic(DICTIONARY.wallets2)}`,
    priceText: '109 TON',
    tonAmount: 109,
  },
];

function ActivateWallet() {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [walletInput, setWalletInput, walletInputError, setWalletInputError] = useInputState('');
  const [usernameInput, setUsernameInput, usernameInputError, setUsernameInputError] = useInputState('');
  const [trLoading, setTrLoading] = useState(false);
  const [, setModalState] = useAtom(modalStateAtom);

  function isTonWalletAddress(address) {
    const tonWalletRegex = /^(eq|kq|uq)[A-Za-z0-9_-]{20,60}$/;

    return tonWalletRegex.test(address.toLowerCase());
  }

  const onGenerateClick = async () => {
    let isError = false;
    if (!walletInput) {
      setWalletInputError(t(DICTIONARY.setWalletAddress));
      isError = true;
    } else {
      for (const wallet of walletInput.replaceAll(' ', '').split(',')) {
        if (!isTonWalletAddress(wallet)) {
          setWalletInputError(t(DICTIONARY.wrongWalletAddress));
          isError = true;
        }
      }
    }
    if (!usernameInput) {
      setUsernameInputError(t(DICTIONARY.setTelegramUsername));
      isError = true;
    }

    if (isError) return;

    const data = await sendRequest({
      request: Api.generateTransaction,
      payload: { amount: PLANS[selectedPlan].tonAmount },
      setLoading: setTrLoading,
    });

    if (data?.status !== 'ok') return;

    setModalState({
      isOpen: true,
      children: <SuccessTransactionModal
        {...data}
        username={usernameInput}
        price={PLANS[selectedPlan].tonAmount}
        addresses={walletInput.replaceAll(' ', '').split(',')}
      />,
    });
  };

  return (
    <div className={styles.wrapper}>
      <p className={styles.header}>{t(DICTIONARY.activateYourWallet)}</p>
      <p className={styles.headerInfo}>
        {t(DICTIONARY.activateYourWalletInfo)}
        <Link
          to="/about"
        >
          {t(DICTIONARY.learn)}
          <img src={RightArrowBlue} alt=">" />
        </Link>
      </p>

      <EmptyHeight height={30} />
      <div className={styles.tableHeader}>
        <p className={styles.tableHeaderTitle}>{t(DICTIONARY.chooseYourPlan)}</p>
      </div>
      <EmptyHeight height={14} />

      <div className={styles.plansRow}>
        {PLANS.map((item, i) => (
          <div
            key={i}
            className={classNames(styles.plansItem, { [styles.plansItemSelected]: i === selectedPlan })}
          >
            <p className={styles.plansTop}>{t(DICTIONARY.youCanActivate)}</p>
            <p className={styles.plansCount}>{item.countText}</p>
            <p className={styles.plansInfo}>{t(DICTIONARY.for)}</p>
            <div className={styles.plansPrice}>
              <img src={TonIcon} />
              <p>{item.priceText}</p>
            </div>
            <MyButton
              text={t(DICTIONARY.select)}
              onClick={() => setSelectedPlan(i)}
              disabled={i === selectedPlan}
            />
          </div>
        ))}
      </div>

      <EmptyHeight height={30} />
      <div className={styles.tableHeader}>
        <p className={styles.tableHeaderTitle}>{selectedPlan === 0 ? t(DICTIONARY.selectAddressOne) : t(DICTIONARY.selectAddressMany)}</p>
      </div>
      <EmptyHeight height={8} />
      <MyInput
        withLeftIcon={false}
        withCloseIcon={false}
        placeholder="Uqd3f0hkc..."
        value={walletInput}
        onChange={setWalletInput}
        error={walletInputError}
        isLarge
        className={styles.mainInput}
        wrapperClassName={styles.mainInput}
      />
      <EmptyHeight height={10} />
      <div className={styles.tableHeader}>
        <p className={styles.tableHeaderTitle}>{t(DICTIONARY.selectContactUsername)}</p>
      </div>
      <EmptyHeight height={8} />
      <MyInput
        withLeftIcon={false}
        withCloseIcon={false}
        placeholder={`${t(DICTIONARY.username).toLowerCase()}...`}
        value={usernameInput}
        onChange={setUsernameInput}
        error={usernameInputError}
      />
      <EmptyHeight height={36} />
      <MyButton
        text={t(DICTIONARY.generateTransaction)}
        onClick={onGenerateClick}
        loading={trLoading}
      />

      <p className={styles.bottomInfo}>{t(DICTIONARY.toReceiveTonCoins)}</p>
    </div>
  );
}

export default ActivateWallet;
